import { logger } from '@/helpers/logger';
import useIsMobile from '@/hooks/useIsMobile/useIsMobile';
import useSlider from '@/hooks/useSlider/useSlider';
import { animated, config, useTransition } from '@react-spring/web';
import clsx from 'clsx';
import React, { useState } from 'react';
import Button from '../../Button/Button';
import { ButtonAlign, ButtonColor } from '../../Button/consts';
import { ClickableAction } from '../../Clickable/consts';
import Icon from '../../Icon/Icon';
import type { MagazineFaqProps } from './interfaces';
import styles from './MagazineFaq.module.scss';
import MagazineFaqBullet from './MagazineFaqBullet/MagazineFaqBullet';

const ANIMATION_DURATION = 250;

const MagazineFaq = ({ items, align }: MagazineFaqProps) => {
  if (items.length === 0) {
    logger.warn('magazine faq requires at least one faq item');
  }

  const { setIndex, currentIndex, currentItem, previousIndex } =
    useSlider(items);
  const isMobile = useIsMobile();

  const isItemActive = (index: number) => currentIndex === index;

  const handleOnClickBullet = (index: number) => () => {
    if (isItemActive(index)) {
      return;
    }
    setIndex(index);
  };

  const alignStyle = align ? styles[align] : '';
  const [isRotated, setIsRotated] = useState(false);

  const transitionMobile = useTransition(currentIndex, {
    from: {
      transform: `translateX(${
        previousIndex.current < currentIndex ? '' : '-'
      }18px)`,
      opacity: 0,
      position: 'relative',
    },
    enter: {
      transform: `translateX(0px)`,
      opacity: 1,
    },
    leave: {
      transform: `translateX(${
        previousIndex.current < currentIndex ? '-' : ''
      }18px)`,
      opacity: 0,
      position: 'absolute',
    },
    config: {
      duration: ANIMATION_DURATION,
      ...config.gentle,
    },
    trail: ANIMATION_DURATION,
    order: ['leave', 'enter', 'update'],
  });

  const transitionDesktop = useTransition(currentIndex, {
    from: {
      transform: `translateY(${
        previousIndex.current < currentIndex ? '' : '-'
      }36px)`,
      opacity: 0,
      position: 'relative',
    },
    enter: {
      transform: `translateY(0px)`,
      opacity: 1,
    },
    leave: {
      transform: `translateY(${
        previousIndex.current < currentIndex ? '-' : ''
      }36px)`,
      opacity: 0,
      position: 'absolute',
    },
    config: {
      duration: ANIMATION_DURATION,
      ...config.gentle,
    },
    trail: ANIMATION_DURATION,
    order: ['leave', 'enter', 'update'],
  });

  const transitions = isMobile ? transitionMobile : transitionDesktop;

  return (
    // TODO ideally this component should implement all a11y requirements for tabs
    <div className={clsx(styles.base, alignStyle)}>
      <div
        className={clsx(styles.inner, {
          [styles.rotated]: isRotated,
        })}
      >
        <div className={styles.front}>
          <div className={styles.content}>
            <div className={styles.bullets}>
              {items.map((_, index) => {
                return (
                  <MagazineFaqBullet
                    key={index}
                    isActive={currentIndex === index}
                    onClick={handleOnClickBullet(index)}
                    aria-label={`Zu Frage ${index + 1} springen`}
                  />
                );
              })}
            </div>
            <div>
              <div>
                <h3 className={styles.questionHeadline}>
                  <span className={styles.lineBreak}>Fragen &amp;</span>{' '}
                  <span className={styles.lineBreak}>Antworten</span>
                </h3>
              </div>
              <div className={styles.questionItem}>
                {transitions(({ position, ...props }, currentIndex) => (
                  <animated.div
                    className={styles.question}
                    style={{
                      position: position as
                        | React.CSSProperties['position']
                        | any,
                      ...props,
                    }}
                  >
                    {items[currentIndex]?.question}
                  </animated.div>
                ))}
              </div>
              <span
                onClick={() => setIsRotated(true)}
                className={styles.button}
              >
                <Icon
                  variant={'action/arrow-right'}
                  size={isMobile ? 'iconSize20' : 'iconSize24'}
                />
                zur Antwort
              </span>
            </div>
          </div>
          <div className={styles.imageWrapper}>
            <div className={styles.image} />
          </div>
        </div>

        <div className={styles.back}>
          <Icon
            variant={'action/close'}
            size="iconSize24"
            onClick={() => setIsRotated(false)}
            className={styles.closeIcon}
          />
          <div>
            <div className={styles.backQuestion}>{currentItem?.question}</div>
            <div className={styles.backAnswer}>{currentItem?.answer}</div>
            {currentItem && currentItem.link && (
              <Button
                actionType={ClickableAction.Linkintern}
                linkintern={currentItem.link}
                color={ButtonColor.Secondary}
                className={styles.buttonArtikel}
                align={isMobile ? ButtonAlign.Left : ButtonAlign.Center}
              >
                zum Artikel
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MagazineFaq;
